import React from 'react';
import { useUser, SignInButton } from '@clerk/clerk-react';
import { loadStripe } from '@stripe/stripe-js';
import { useAction, useQuery } from 'convex/react';
import { useToast } from "@/components/ui/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { Button } from '@/components/ui/button';
import { SERVICE_TIERS } from '../../convex/constants/serviceTiers';
import { api } from '../../convex/_generated/api';
import { Check, Zap, CreditCard, ExternalLink, CheckCircle2, BadgeCheck, Sparkles, CircleDollarSign, LucideCalendar, GaugeCircle } from 'lucide-react';
import UsageIndicator from './UsageIndicator';
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type SubscriptionStatus = "active" | "canceled" | "past_due" | undefined;

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "");

const PRICE_LOOKUP_KEYS = {
  "pro-monthly": "pro-monthly",
  "pro-yearly": "pro-yearly",
  "enterprise-monthly": "enterprise-monthly",
  "enterprise-yearly": "enterprise-yearly",
} as const;

const TIER_PRICES = {
  pro: {
    monthly: "$9.95/month",
    yearly: "$79.95/year",
    regularMonthly: "$24.95/month",
    regularYearly: "$249.50/year",
  },
  enterprise: {
    monthly: "$49.95/month",
    yearly: "$379/year",
    regularMonthly: "$110/month",
    regularYearly: "$1,100/year",
  },
};

export default function SubscriptionManager() {
  const { user, isLoaded, isSignedIn } = useUser();
  // Only query preferences if user is signed in
  const preferences = useQuery(api.posts.getUserPreferences, isSignedIn ? undefined : "skip");
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [billingInterval, setBillingInterval] = React.useState<'monthly' | 'yearly'>('monthly');
  const createCheckoutSession = useAction(api.stripe.createCheckoutSession.createCheckoutSession);
  const createPortalSession = useAction(api.stripe.createPortalSession.createPortalSession);

  // Use Convex data as source of truth, fall back to Clerk metadata
  const currentTier = (preferences?.serviceTier || user?.privateMetadata?.serviceTier || 'free') as keyof typeof SERVICE_TIERS;
  const subscriptionStatus = preferences?.subscriptionStatus as SubscriptionStatus;
  const hasActiveSubscription = isSignedIn && currentTier !== 'free' && subscriptionStatus === 'active';
  const hasCanceledSubscription = isSignedIn && currentTier !== 'free' && subscriptionStatus === 'canceled';
  const hasPastDueSubscription = isSignedIn && currentTier !== 'free' && subscriptionStatus === 'past_due';
  
  // Always show manage button if the user is signed in
  const showManageButton = isSignedIn;

  const handleManageSubscription = async () => {
    try {
      if (!user?.id) {
        throw new Error("User not found");
      }

      setIsLoading(true);
      const { url } = await createPortalSession({
        userId: user.id,
        returnUrl: window.location.origin,
      });

      window.location.href = url;
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to access subscription portal",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (priceKey: string) => {
    setIsLoading(true);
    try {
      if (!user?.id) {
        throw new Error("User not found");
      }

      const billingKey = `${priceKey}-${billingInterval}` as keyof typeof PRICE_LOOKUP_KEYS;

      const { sessionId } = await createCheckoutSession({
        priceKey: billingKey,
        userId: user.id,
        returnUrl: window.location.origin,
      });

      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe not loaded");

      await stripe.redirectToCheckout({ sessionId });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to start checkout process",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get status badge color
  const getStatusBadgeColor = (status?: string) => {
    if (!status) return "bg-gray-100 text-gray-800";
    switch(status) {
      case "active":
        return "bg-green-100 text-green-800 border border-green-200";
      case "canceled":
        return "bg-red-100 text-red-800 border border-red-200";
      case "past_due":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Format the savings amount
  const calculateSavings = (tier: 'pro' | 'enterprise') => {
    const regular = billingInterval === 'monthly'
      ? parseFloat(TIER_PRICES[tier].regularMonthly.replace('$', '').replace('/month', ''))
      : parseFloat(TIER_PRICES[tier].regularYearly.replace('$', '').replace('/year', ''));
      
    const current = billingInterval === 'monthly'
      ? parseFloat(TIER_PRICES[tier].monthly.replace('$', '').replace('/month', ''))
      : parseFloat(TIER_PRICES[tier].yearly.replace('$', '').replace('/year', ''));
      
    const savings = regular - current;
    const savingsPercent = Math.round((savings / regular) * 100);
    
    return {
      amount: `$${savings.toFixed(0)}`,
      percent: `${savingsPercent}%`
    };
  };

  return (
    <div className="py-12 bg-gradient-to-b from-cream to-white rounded-xl">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-black via-dogbrown to-dogorange">
            Subscription Management
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Choose the plan that fits your content creation needs and scale your business with high-quality, SEO-optimized content.
          </p>
        </div>

        {/* Current Subscription Status & Usage */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-50 to-transparent -z-10 rounded-bl-full"></div>
            
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {/* Left side: Plan info */}
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-dogbrown/10">
                    <GaugeCircle className="w-6 h-6 text-dogbrown" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Current Plan</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-medium text-lg">{SERVICE_TIERS[currentTier].name}</span>
                      {subscriptionStatus && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(subscriptionStatus)}`}>
                          {subscriptionStatus === 'active' ? 'Active' : 
                           subscriptionStatus === 'past_due' ? 'Past Due' : 
                           'Canceled'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-full bg-blue-100">
                      <LucideCalendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="font-medium">Monthly Usage</span>
                        <span className="text-sm font-semibold">
                          {preferences?.monthlyUsage || 0} / {preferences?.monthlyLimit || SERVICE_TIERS[currentTier].monthlyLimit} posts
                        </span>
                      </div>
                      <div className="mt-2 w-full">
                        <UsageIndicator />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 mt-4">
                    <div className="p-2 rounded-full bg-green-100">
                      <CircleDollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <span className="font-medium">Features</span>
                      <div className="mt-1 text-sm text-gray-700 space-y-1">
                        <div className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span>Up to {SERVICE_TIERS[currentTier].features.maxWordCount} words per post</span>
                        </div>
                        {SERVICE_TIERS[currentTier].features.marketerMode && (
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span>Marketer Mode enabled</span>
                          </div>
                        )}
                        {SERVICE_TIERS[currentTier].features.prioritySupport && (
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span>Priority support</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right side: Action buttons */}
              {showManageButton && (
                <div className="flex flex-col gap-3 w-full md:w-auto">
                  <Button
                    variant="default"
                    onClick={handleManageSubscription}
                    disabled={isLoading}
                    className="flex items-center gap-2 bg-gradient-to-r from-dogblue to-blue-700 hover:from-dogblue/90 hover:to-blue-700/90 text-white shadow-lg shadow-blue-200 px-6 py-5 text-base"
                  >
                    {isLoading ? (
                      <>Processing...</>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        Manage Subscription
                        <ExternalLink className="w-3 h-3 ml-1 opacity-70" />
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    Manage billing, update payment methods, or change plans
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
          
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold mb-3">Choose Your Plan</h2>
          <p className="text-gray-600 mb-4 max-w-2xl mx-auto">
            {hasActiveSubscription 
              ? "You can change your plan at any time. Upgrades take effect immediately, downgrades at the end of your billing period."
              : "Select the plan that works best for your content needs."
            }
          </p>
          
          {/* Special Offers Alert */}
          <div className="max-w-xl mx-auto">
            <div className="bg-gradient-to-r from-yellow-100 to-amber-100 border border-amber-200 p-4 rounded-lg mb-6 flex items-center gap-3 shadow-sm">
              <div className="bg-amber-500/20 p-1.5 rounded-full">
                <Sparkles className="w-5 h-5 text-amber-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-amber-800">Founder Launch Pricing! 🚀</p>
                <p className="text-sm text-amber-700">
                  For a limited time, enjoy up to <span className="font-semibold">80% off</span> our regular price!
                </p>
              </div>
            </div>
          </div>
          
          {/* Billing Interval Selector */}
          <div className="inline-flex items-center p-1.5 bg-gray-100 rounded-lg shadow-inner mb-8">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                billingInterval === 'monthly'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
              }`}
            >
              Monthly billing
            </button>
            <button
              onClick={() => setBillingInterval('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 relative ${
                billingInterval === 'yearly'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
              }`}
            >
              Yearly billing
              <span className="absolute -top-3 -right-2 px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full shadow-sm">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <Tabs defaultValue="cards" className="max-w-5xl mx-auto">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="cards">Card View</TabsTrigger>
            <TabsTrigger value="compare">Comparison</TabsTrigger>
          </TabsList>
          
          <TabsContent value="cards" className="pt-6">
            <div className="grid md:grid-cols-2 gap-8">
              {(['pro', 'enterprise'] as const).map((tier) => {
                // Check if this is the current tier
                const isTierCurrent = currentTier === tier;
                const isHighlighted = isTierCurrent && hasActiveSubscription;
                const tierDetails = SERVICE_TIERS[tier];
                const savings = calculateSavings(tier);

                return (
                  <motion.div 
                    key={tier}
                    className={`rounded-xl shadow-lg overflow-hidden border bg-white relative ${
                      isHighlighted ? 'ring-4 ring-dogblue/30 transform scale-[1.02]' : ''
                    }`}
                    whileHover={{ 
                      y: -5,
                      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
                    }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    {/* Founder pricing banner */}
                    <div className="bg-gradient-to-r from-dogbrown to-amber-600 text-white py-2 text-center shadow-sm">
                      <span className="text-xs font-bold uppercase tracking-wider">Founder Launch Pricing</span>
                    </div>
                    
                    {/* Current plan badge */}
                    {isTierCurrent && hasActiveSubscription && (
                      <div className="absolute top-2 right-2 z-10">
                        <span className="inline-flex items-center gap-1 bg-dogblue text-white text-xs font-bold px-2.5 py-1.5 rounded-full shadow-lg">
                          <BadgeCheck className="w-3 h-3" />
                          CURRENT PLAN
                        </span>
                      </div>
                    )}
                    
                    <div className="p-6 pt-8">
                      <div className="flex items-start">
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            {tier === 'pro' ? (
                              <Zap className="w-5 h-5 text-dogblue" />
                            ) : (
                              <Sparkles className="w-5 h-5 text-dogbrown" />
                            )}
                            <span>{SERVICE_TIERS[tier].name}</span>
                          </h3>
                          
                          <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-baseline">
                              <span className={`text-3xl font-extrabold ${tier === 'pro' ? 'text-dogblue' : 'text-dogbrown'}`}>
                                {TIER_PRICES[tier][billingInterval].split('/')[0]}
                              </span>
                              <span className="text-base font-medium text-gray-500 ml-1">
                                {billingInterval === 'yearly' ? '/year' : '/month'}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center gap-2">
                              <s className="text-gray-400 text-sm">Regular: {billingInterval === 'yearly' ? 
                                TIER_PRICES[tier].regularYearly : 
                                TIER_PRICES[tier].regularMonthly}
                              </s>
                              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                                Save {savings.percent}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className={`w-16 h-16 ${tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'} rounded-full flex items-center justify-center`}>
                          <span className={`text-2xl font-bold ${tier === 'pro' ? 'text-blue-700' : 'text-amber-700'}`}>
                            {tier === 'pro' ? 'P' : 'E'}
                          </span>
                        </div>
                      </div>

                      <ul className="mt-6 space-y-3 text-sm">
                        <li className="flex items-start gap-2">
                          <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                            <Check className={`w-3 h-3 ${tier === 'pro' ? 'text-blue-700' : 'text-amber-700'}`} />
                          </div>
                          <span className="text-gray-700 text-sm">
                            <strong>{tierDetails.monthlyLimit}</strong> posts per month
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                            <Check className={`w-3 h-3 ${tier === 'pro' ? 'text-blue-700' : 'text-amber-700'}`} />
                          </div>
                          <span className="text-gray-700 text-sm">
                            <strong>{tierDetails.features.maxWordCount}</strong> words per post
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                            <Check className={`w-3 h-3 ${tier === 'pro' ? 'text-blue-700' : 'text-amber-700'}`} />
                          </div>
                          <span className="text-gray-700 text-sm">
                            {tier === 'pro' 
                              ? 'Access to advanced AI models' 
                              : 'Access to all AI models'}
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                            <Check className={`w-3 h-3 ${tier === 'pro' ? 'text-blue-700' : 'text-amber-700'}`} />
                          </div>
                          <span className="text-gray-700 text-sm">
                            Custom writing styles
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                            <Check className={`w-3 h-3 ${tier === 'pro' ? 'text-blue-700' : 'text-amber-700'}`} />
                          </div>
                          <span className="text-gray-700 text-sm">
                            Strike list feature
                          </span>
                        </li>
                        {tierDetails.features.marketerMode && (
                          <li className="flex items-start gap-2">
                            <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                              <Check className={`w-3 h-3 ${tier === 'pro' ? 'text-blue-700' : 'text-amber-700'}`} />
                            </div>
                            <span className="text-gray-700 text-sm">
                              Marketer mode
                            </span>
                          </li>
                        )}
                        {tierDetails.features.prioritySupport && (
                          <li className="flex items-start gap-2">
                            <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                              <Check className={`w-3 h-3 ${tier === 'pro' ? 'text-blue-700' : 'text-amber-700'}`} />
                            </div>
                            <span className="text-gray-700 text-sm">
                              Priority support
                            </span>
                          </li>
                        )}
                      </ul>

                      {!isSignedIn ? (
                        <SignInButton mode="modal">
                          <Button className="mt-8 w-full bg-black hover:bg-black/90 py-6">
                            Sign in to upgrade
                          </Button>
                        </SignInButton>
                      ) : isLoading ? (
                        <Button className="mt-8 w-full py-6" disabled>
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </div>
                        </Button>
                      ) : isTierCurrent && hasActiveSubscription ? (
                        <div className="mt-8 bg-blue-100/50 rounded-lg p-4 text-center border border-blue-200">
                          <p className="text-sm font-medium text-blue-800 flex items-center justify-center gap-1.5">
                            <BadgeCheck className="w-4 h-4 text-blue-700" />
                            You're currently on this plan
                          </p>
                        </div>
                      ) : (
                        <Button
                          className={`mt-8 w-full py-6 ${
                            tier === 'pro' 
                              ? 'bg-dogblue hover:bg-dogblue/90' 
                              : 'bg-dogbrown hover:bg-dogbrown/90'
                          }`}
                          onClick={() => handleSubscribe(tier)}
                          disabled={isLoading}
                        >
                          {hasActiveSubscription ? (
                            isTierCurrent ? 'Current Plan' : `Switch to ${SERVICE_TIERS[tier].name}`
                          ) : (
                            `Upgrade to ${SERVICE_TIERS[tier].name}`
                          )}
                        </Button>
                      )}
                    </div>
                    <div className="py-3 px-6 bg-gray-50 text-gray-500 text-xs text-center border-t">
                      <span className="italic">Limited time offer - regular price will apply after launch period</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>
          
          <TabsContent value="compare" className="pt-6">
            <div className="bg-white rounded-xl shadow-md overflow-hidden border">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700 border-b">Feature</th>
                    <th className="py-4 px-6 text-center text-sm font-semibold text-gray-700 border-b">Free</th>
                    <th className="py-4 px-6 text-center text-sm font-semibold text-dogblue border-b">Pro</th>
                    <th className="py-4 px-6 text-center text-sm font-semibold text-dogbrown border-b">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">Monthly Post Limit</td>
                    <td className="py-4 px-6 text-sm text-center">3</td>
                    <td className="py-4 px-6 text-sm text-center font-medium text-dogblue">15</td>
                    <td className="py-4 px-6 text-sm text-center font-medium text-dogbrown">100</td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">Max Words Per Post</td>
                    <td className="py-4 px-6 text-sm text-center">2,000</td>
                    <td className="py-4 px-6 text-sm text-center font-medium text-dogblue">4,000</td>
                    <td className="py-4 px-6 text-sm text-center font-medium text-dogbrown">4,000</td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">AI Models</td>
                    <td className="py-4 px-6 text-sm text-center">Basic</td>
                    <td className="py-4 px-6 text-sm text-center font-medium text-dogblue">Advanced</td>
                    <td className="py-4 px-6 text-sm text-center font-medium text-dogbrown">Premium</td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">Custom Writing Styles</td>
                    <td className="py-4 px-6 text-sm text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                    <td className="py-4 px-6 text-sm text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                    <td className="py-4 px-6 text-sm text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">Strike List</td>
                    <td className="py-4 px-6 text-sm text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                    <td className="py-4 px-6 text-sm text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                    <td className="py-4 px-6 text-sm text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">Marketer Mode</td>
                    <td className="py-4 px-6 text-sm text-center">
                      <div className="w-5 h-5 mx-auto text-gray-300">—</div>
                    </td>
                    <td className="py-4 px-6 text-sm text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                    <td className="py-4 px-6 text-sm text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">Priority Support</td>
                    <td className="py-4 px-6 text-sm text-center">
                      <div className="w-5 h-5 mx-auto text-gray-300">—</div>
                    </td>
                    <td className="py-4 px-6 text-sm text-center">
                      <div className="w-5 h-5 mx-auto text-gray-300">—</div>
                    </td>
                    <td className="py-4 px-6 text-sm text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-4 px-6 text-base font-semibold text-gray-900">Monthly Price</td>
                    <td className="py-4 px-6 text-center font-medium">
                      <div className="text-gray-700">Free</div>
                    </td>
                    <td className="py-4 px-6 text-center font-bold">
                      <div className="text-dogblue">{TIER_PRICES.pro.monthly.split('/')[0]}</div>
                      <div className="text-xs text-gray-500"><s>Reg: {TIER_PRICES.pro.regularMonthly.split('/')[0]}</s></div>
                    </td>
                    <td className="py-4 px-6 text-center font-bold">
                      <div className="text-dogbrown">{TIER_PRICES.enterprise.monthly.split('/')[0]}</div>
                      <div className="text-xs text-gray-500"><s>Reg: {TIER_PRICES.enterprise.regularMonthly.split('/')[0]}</s></div>
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-4 px-6 text-base font-semibold text-gray-900">Annual Price (Save 20%)</td>
                    <td className="py-4 px-6 text-center font-medium">
                      <div className="text-gray-700">Free</div>
                    </td>
                    <td className="py-4 px-6 text-center font-bold">
                      <div className="text-dogblue">{TIER_PRICES.pro.yearly.split('/')[0]}</div>
                      <div className="text-xs text-gray-500"><s>Reg: {TIER_PRICES.pro.regularYearly.split('/')[0]}</s></div>
                    </td>
                    <td className="py-4 px-6 text-center font-bold">
                      <div className="text-dogbrown">{TIER_PRICES.enterprise.yearly.split('/')[0]}</div>
                      <div className="text-xs text-gray-500"><s>Reg: {TIER_PRICES.enterprise.regularYearly.split('/')[0]}</s></div>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              {/* Action buttons below table */}
              {isSignedIn && (
                <div className="p-6 flex flex-col sm:flex-row justify-center gap-4">
                  {!currentTier || currentTier === 'free' ? (
                    <>
                      <Button
                        className="bg-dogblue hover:bg-dogblue/90 py-5 px-8 text-white font-medium"
                        onClick={() => handleSubscribe('pro')}
                        disabled={isLoading}
                      >
                        Upgrade to Pro
                      </Button>
                      <Button
                        className="bg-dogbrown hover:bg-dogbrown/90 py-5 px-8 text-white font-medium"
                        onClick={() => handleSubscribe('enterprise')}
                        disabled={isLoading}
                      >
                        Upgrade to Enterprise
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="default"
                      onClick={handleManageSubscription}
                      disabled={isLoading}
                      className="py-5 px-8 text-base font-medium"
                    >
                      Manage Current Subscription
                    </Button>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-5 border border-gray-200">
              <h3 className="font-medium text-lg mb-2">How does billing work?</h3>
              <p className="text-gray-700 text-sm">
                You'll be charged at the beginning of each billing cycle. You can change your plan at any time - upgrades take effect immediately while downgrades take effect at the end of your current billing period.
              </p>
            </div>
            <div className="bg-white rounded-lg p-5 border border-gray-200">
              <h3 className="font-medium text-lg mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-700 text-sm">
                Yes! You can cancel your subscription at any time. You'll continue to have access to your current plan until the end of your billing period.
              </p>
            </div>
            <div className="bg-white rounded-lg p-5 border border-gray-200">
              <h3 className="font-medium text-lg mb-2">Do unused posts roll over?</h3>
              <p className="text-gray-700 text-sm">
                No, your monthly post allowance resets at the beginning of each billing cycle. Make the most of your subscription by using all your available posts each month.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-16 max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 border border-blue-100 rounded-xl p-6 text-center shadow-sm">
            <p className="text-sm text-gray-500">
              Need a custom plan for your team or agency? <a href="/contact" className="text-blue-600 hover:underline">Contact our sales team</a> for enterprise options with higher limits and additional features.
            </p>
          </div>
        </div>
      </div>
      <Toaster />
    </div>
  );
}